/**
 * Welcome-letter font + size picker for CP and admin welcome-email screens.
 */

import React, { useEffect, useState } from 'react';
import { Settings } from 'lucide-react';

export const LETTER_FONT_LABELS: Record<string, string> = {
  calibri: 'Calibri',
  arial: 'Arial',
  segoe: 'Segoe UI',
  verdana: 'Verdana',
  georgia: 'Georgia',
  times: 'Times New Roman',
  trebuchet: 'Trebuchet MS',
};

export const DEFAULT_LETTER_FONTS = Object.keys(LETTER_FONT_LABELS);
export const DEFAULT_LETTER_SIZES = ['10pt', '11pt', '12pt', '14pt'];

interface Props {
  fontFamily: string;
  fontSize: string;
  fontOptions?: string[];
  sizeOptions?: string[];
  disabled?: boolean;
  onApply: (fontFamily: string, fontSize: string) => void;
}

export const LetterStyleConfigButton: React.FC<Props> = ({
  fontFamily,
  fontSize,
  fontOptions = DEFAULT_LETTER_FONTS,
  sizeOptions = DEFAULT_LETTER_SIZES,
  disabled = false,
  onApply,
}) => {
  const [open, setOpen] = useState(false);
  const [draftFont, setDraftFont] = useState(fontFamily);
  const [draftSize, setDraftSize] = useState(fontSize);

  useEffect(() => {
    if (open) {
      setDraftFont(fontFamily);
      setDraftSize(fontSize);
    }
  }, [open, fontFamily, fontSize]);

  const apply = () => {
    onApply(draftFont, draftSize);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5"
      >
        <Settings className="w-4 h-4" aria-hidden="true" />
        Letter style
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-labelledby="letter-style-title"
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4"
          >
            <h4 id="letter-style-title" className="font-semibold text-lg">Letter style</h4>
            <p className="text-sm text-gray-600">
              Choose the font and size used in the welcome letter. Apply saves a draft and refreshes the preview.
            </p>
            <div>
              <label htmlFor="letter-font-family" className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Font
              </label>
              <select
                id="letter-font-family"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={draftFont}
                onChange={(e) => setDraftFont(e.target.value)}
              >
                {fontOptions.map((key) => (
                  <option key={key} value={key}>
                    {LETTER_FONT_LABELS[key] || key}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="letter-font-size" className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Size
              </label>
              <select
                id="letter-font-size"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={draftSize}
                onChange={(e) => setDraftSize(e.target.value)}
              >
                {sizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size.replace('pt', ' pt')}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border rounded-lg">
                Cancel
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={apply}
                className="px-4 py-2 text-sm bg-[var(--primary)] text-white rounded-lg disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LetterStyleConfigButton;
